import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http'
import {Observable} from 'rxjs/Rx';

import {AuthTokenDto} from "./dtos/auth-token.dto";
import {UserDto} from "./dtos/user.dto";
import {LibraryGroupDto} from "./dtos/library-group.dto";
import {DashboardDto} from "./dtos/dashboard.dto";
import {BranchDto} from "./dtos/branch.dto";
import {UserSessionService} from "./user-session.service";
import {GateCountReportDto} from "./dtos/gate-count-report.dto";
import {SearchDto} from "./dtos/search.dto";
import {DashboardTerminalDto} from "./dtos/dashboard-terminal.dto";
import {PortalLinkDto} from "./dtos/portal-link.dto";
import {AutocompleteValuesDto} from "./dtos/licencing/autocomplete-values.dto";
import {CreateLicenceRequestDto} from "./dtos/licencing/create-licence-request.dto";
import {ConfigDto} from "./dtos/config.dto";
import {ApplicationDto} from "./dtos/application.dto";
import {DeploymentDto} from "./dtos/deployment.dto";
import {CreateLicenceResponseDto} from "./dtos/licencing/create-licence-response.dto";
import {SmartLibraryApplication} from "./dtos/enums/smart-library-application.enum";
import {DownloadUrlDto} from "./dtos/download-url.dto";
import {HierarchyDto} from "./dtos/hierarchy.dto";
import {ReportDto} from "./dtos/reports/report.dto";

@Injectable()
export class CloudApiService {

    public onUnauthorised: (isFromBadLogin: boolean) => void;

    constructor(
        private http: HttpClient,
        private userSession: UserSessionService
    ) {
        userSession.library.subscribe(library => {
            if (!library)
                return;

            this.libraryId = library.id;
            this.urlConfigs = this.urlLibraries + '/' + this.libraryId + '/configs'
            this.urlBranches = this.urlLibraries + '/' + this.libraryId + '/branches';
            this.urlTerminals = this.urlLibraries + '/' + this.libraryId + '/terminals';
            this.urlDeployments = this.urlLibraries + '/' + this.libraryId + '/deployments';
            this.urlDeploymentsBlank = this.urlLibraries + '/0/deployments';
            this.urlPortal = this.urlLibraries + '/' + this.libraryId + '/portal';
        });
    }

    /*** Authenticate ***/

    authenticate(authenticationData: Object): Observable<AuthTokenDto> {
        return this.http.post(this.urlAuthenticate, this.formatFormData(authenticationData), {headers: this.headerFormContent})
            .catch(error => this.handleError(error, true));
    };

    /*** Refresh Token ***/

    deleteRefreshToken(refreshToken: string): Observable<void> {
        return this.http.delete(this.urlRefreshTokens + '/' + refreshToken)
            .catch(error => this.handleError(error));
    }

    /*** User Password Reset ***/

    getUserByPasswordResetToken(passwordResetToken: string): Observable<UserDto> {
        return this.http.get(this.urlUsersPasswordReset + '/' + passwordResetToken)
            .catch(error => this.handleError(error));
    };

    resetUserPassword(passwordResetToken: string, password: string): Observable<void> {
        return this.http.post(this.urlUsersPasswordReset + '/' + passwordResetToken, JSON.stringify(password), {headers: this.headerJsonContent})
            .catch(error => this.handleError(error));
    };

    /*** Users ***/

    getUsers(): Observable<UserDto[]> {
        return this.http.get(this.urlUsers)
            .catch(error => this.handleError(error));
    }

    getUsersGlobalRoles(): Observable<UserDto[]> {
        return this.http.get(this.urlUsers + '/global')
            .catch(error => this.handleError(error));
    }

    getUsersByLibrary(libraryID: number): Observable<UserDto[]> {
        return this.http.get(this.urlUsers + '/library/' + libraryID)
            .catch(error => this.handleError(error));
    }

    getUser(id: number): Observable<UserDto> {
        return this.http.get(this.urlUsers + '/' + id)
            .catch(error => this.handleError(error));
    }

    createUserPasswordResetToken(id: number): Observable<string> {
        return this.http.post(this.urlUsers + '/' + id + '/passwordreset', undefined, {responseType: 'text'})
            .catch(error => this.handleError(error));
    }

    createUser(user: UserDto) {
        return this.http.post(this.urlUsers, user)
            .catch(error => this.handleError(error));
    }

    editUser(user: UserDto): Observable<UserDto[]> {
        return this.http.post(this.urlUsers, user)
            .catch(error => this.handleError(error));
    }

    deleteUser(id: number): Observable<UserDto[]> {
        return this.http.post(this.urlUsers + '/' + id + '/delete', undefined)
            .catch(error => this.handleError(error));
    }

    /*** Libraries ***/

    getLibraries(): Observable<LibraryGroupDto[]> {
        return this.http.get(this.urlLibraries)
            .catch(error => this.handleError(error));
    };

    getLibrary(id: number): Observable<LibraryGroupDto> {
        return this.http.get(this.urlLibraries + '/' + id)
            .catch(error => this.handleError(error));
    };

    /*** Branches ***/

    getBranches(): Observable<BranchDto[]> {
        return this.http.get(this.urlBranches)
            .catch(error => this.handleError(error));
    };

    getBranch(id: number): Observable<BranchDto> {
        return this.http.get(this.urlBranches + '/' + id)
            .catch(error => this.handleError(error));
    };

    /*** Deployments ***/

    getDeploymentApplications(): Observable<ApplicationDto[]> {
        return this.http.get(this.urlDeployments + '/applications')
            .catch(error => this.handleError(error));
    }

    updateApplication(deploymentRequest: DeploymentDto): Observable<DeploymentDto> {
        return this.http.post(this.urlDeployments + '/deploy', deploymentRequest)
            .catch(error => this.handleError(error));
    }

    updateApplications(deploymentRequests: DeploymentDto[]): Observable<DeploymentDto[]> {
        return this.http.post(this.urlDeployments + '/deploy-many', deploymentRequests)
            .catch(error => this.handleError(error));
    }

    cancelDeployment(application: ApplicationDto): Observable<boolean> {
        return this.http.post(this.urlDeployments + '/cancel', application)
            .catch(error => this.handleError(error));
    }

    installNewApplications(applications: ApplicationDto[]) : Observable<ApplicationDto[]> {
        return this.http.post(this.urlDeployments + '/deploy-new', applications)
            .catch(error => this.handleError(error));
    }

    getWhitelistedApplications(): Observable<SmartLibraryApplication[]> {
        return this.http.get(this.urlDeployments + '/whitelisted-applications')
            .catch(error => this.handleError(error));
    }

    getApplicationVersionData(): Observable<string[]> {
        return this.http.get(this.urlDeployments + '/version')
            .catch(error => this.handleError(error));
    }

    getConfigHierarchy(): Observable<HierarchyDto> {
        return this.http.get(this.urlDeployments + '/hierarchy')
            .catch(error => this.handleError(error));
    }

    getPendingDeployments(): Observable<DeploymentDto[]> {
        return this.http.get(this.urlDeployments + '/pending')
            .catch(error => this.handleError(error));
    }

    getRecentDeployments(): Observable<DeploymentDto[]> {
        return this.http.get(this.urlDeployments + '/recent')
            .catch(error => this.handleError(error));
    }

    getDownloadUrl(version: string): Observable<DownloadUrlDto> {
        return this.http.get(`${this.urlDeployments}/download-url?version=${encodeURIComponent(version)}`)
            .catch(error => this.handleError(error));
    }

    /*** Dashboard ***/

    getDashboard(branchId: number, search: SearchDto): Observable<DashboardDto> {
        let url = this.buildBranchUrl(branchId, 'dashboard')
        return this.http.post(url, search)
            .catch(error => this.handleError(error));
    }

    getDashboardReport(branchId: number, search: SearchDto): Observable<GateCountReportDto[]> {
        let url = this.buildBranchUrl(branchId, 'dashboardreports')
        return this.http.post(url, search)
            .catch(error => this.handleError(error));
    }

    getDashboardTerminals(branchId: number): Observable<DashboardTerminalDto[]> {
        let url = this.buildBranchUrl(branchId, 'dashboard/terminals')
        return this.http.get(url)
            .catch(error => this.handleError(error));
    }

    /*** Link Portal ***/

    getPortalLinks(): Observable<PortalLinkDto[]> {
        return this.http.get(this.urlPortal)
            .catch(error => this.handleError(error));
    }

    createPortalLink(portalLink: PortalLinkDto) {
        let url = this.urlPortal + '/create';
        return this.http.post(url, portalLink)
            .catch(error => this.handleError(error));
    }

    deletePortalLink(linkId: number) {
        let url = this.urlPortal + '/' + linkId;
        return this.http.delete(url)
            .catch(error => this.handleError(error));
    }

    /*** Reports ***/

    getReport<TRow>(endpoint: string, branchId: number, search: SearchDto): Observable<ReportDto<TRow>> {
        let url = this.buildBranchUrl(branchId, endpoint);
        return this.http.post(url, search)
            .catch(error => this.handleError(error));
    }

    /*** Licencing API ***/

    licencingGetAutocompleteValues(): Observable<AutocompleteValuesDto> {
        let url = this.getLicencingApiUrl() + '/licences/autocomplete';
        return this.http.get(url)
          .catch(error => this.handleError(error));
    }

    licencingCreateLicence(request: CreateLicenceRequestDto) : Observable<CreateLicenceResponseDto> {
        let url = this.getLicencingApiUrl() + '/licences/create';
        return this.http.post(url, request)
          .catch(error => this.handleError(error));
    }

    /*** Private ***/

    private headerJsonContent = new HttpHeaders({'Content-Type': 'application/json'});
    private headerFormContent = new HttpHeaders({'Content-Type': 'application/x-www-form-urlencoded'});

    private urlBase = this.getApiUrl();
    private urlAuthenticate = this.urlBase + 'auth/token';
    private urlRefreshTokens = this.urlBase + 'refreshtokens';
    private urlUsers = this.urlBase + 'users';
    private urlUsersPasswordReset = this.urlUsers + '/reset';
    private urlLibraries = this.urlBase + 'libraries';

    private libraryId: number;
    private urlConfigs: string;
    private urlBranches: string;
    private urlTerminals: string;
    private urlApplications: string;
    private urlDeployments: string;
    private urlDeploymentsBlank: string;
    private urlPortal: string;

    private getLicencingApiUrl(): string {
        return this.isRunningDebug() ? 'http://localhost:7071/api' : 'https://api.fetechgroup.com/licencing';
    }

    private getApiUrl() {
        return this.getRelativeUrl('api/');
    }

    getRelativeUrl(value: string ) {
        let localUrl = "https://localhost:44301/";
        let webpackSignature = "localhost:8080";

        // Check if running from webpack then use localhost instance (CORS will need to be disabled on the server)
        if (document.URL.indexOf(webpackSignature) >= 0)
            return localUrl + value;

        return value;
    }

    private isRunningDebug(): boolean {
        return (document.URL.includes('://localhost'));
    }

    private handleError(response: any, isFromBadLogin: boolean = false): Observable<any> {
        console.error('Cloud API error', response);

        let errorMessage: string;

        if (response instanceof HttpErrorResponse) {
            if (response.status <= 0)
                errorMessage = 'Failed to contact server.';
            else if (response.error)
                errorMessage = (typeof response.error === 'string') ? response.error : JSON.stringify(response.error);
            else
                errorMessage = 'HTTP error ' + response.status + '.'

            // Raise event when we receive an unauthorised response
            if (this.onUnauthorised && response.status == 401)
                this.onUnauthorised(isFromBadLogin);
        } else {
            errorMessage = JSON.stringify(response);
        }

        return Observable.throw(errorMessage);
    }

    private formatFormData(data: Object): string {
        let returnData = '';
        let count = 0;
        for (let i in data){
            if(count == 0){
                returnData += i+'=' + encodeURIComponent(data[i]);
            }else{
                returnData += '&'+i+'=' + encodeURIComponent(data[i]);
            }
            count = count + 1;
        }
        return returnData;
    }

    private buildBranchUrl(branchId: number, endpoint: string): string {
        let url = this.urlBranches;

        if (branchId > 0 )
            url += '/' + branchId;

        return url + `/${endpoint}`;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/services/cloud-api.service.ts